/** @file StatusBar: open puppet, selection, tool, space and pivot, live socket, pending batches, error. */
import type { ReactElement } from 'react';
import { useEditor } from '../state/useEditor';
import { buildInfo } from './buildInfo';

const LIVE_TEXT = { off: 'live: off', connecting: 'live: connecting', open: 'live: on' } as const;

/** What the strip says about the selection. */
const selectionText = (nodes: string[]): string => (nodes.length === 0 ? 'nothing selected' : nodes.length === 1 ? (nodes[0] ?? '') : `${String(nodes.length)} nodes`);

/** `v<version> · <sha>` once the server told its version, else `build <sha>`. */
const buildText = (sha: string, version: string | null): string => (version === null ? `build ${sha}` : `v${version} · ${sha}`);

/** The 24 px strip at the bottom; `lastError` shows in red at the right, then `v<version> · <sha>` (the server's version once it answered). */
export function StatusBar({ serverVersion = null }: { serverVersion?: string | null }): ReactElement {
  const nodes = useEditor((s) => s.selection.nodes);
  const tool = useEditor((s) => s.tool);
  const space = useEditor((s) => s.space);
  const pivot = useEditor((s) => s.pivot);
  const puppet = useEditor((s) => (s.puppet.id === null ? null : (s.puppet.doc?.name ?? s.puppet.id)));
  const live = useEditor((s) => s.puppet.live);
  const pending = useEditor((s) => s.puppet.pending);
  const lastError = useEditor((s) => s.puppet.lastError);
  const owner = useEditor((s) => s.session.owner);
  const build = buildInfo();
  const selection = selectionText(nodes);
  return (
    <footer className="p3-statusbar" aria-label="Status">
      <span>{puppet === null ? 'no puppet open' : `puppet: ${puppet}`}</span>
      <span>{selection}</span>
      <span>tool: {tool}</span>
      <span>{space} / {pivot}</span>
      <span>{LIVE_TEXT[live]}</span>
      {pending > 0 ? <span>sending {pending}</span> : null}
      <span className="p3-spacer" />
      {lastError === null ? null : <span className="p3-error" title={lastError}>{lastError}</span>}
      {owner === null ? null : <span>{owner}</span>}
      <span title={`Editor build ${build.sha}${build.at === '' ? '' : ` of ${build.at}`}`}>{buildText(build.sha, serverVersion)}</span>
    </footer>
  );
}
